import { translations, Language, Translations } from './i18n';

interface TranslationSnapshot {
  id: string;
  timestamp: number;
  translations: Record<Language, Translations>;
  checksum: string;
  source: 'auto' | 'manual' | 'pre-update';
}

interface BackupMetadata {
  totalSnapshots: number;
  lastBackup: number;
  lastRestoration: number | null;
  autoBackupEnabled: boolean;
}

class TranslationBackupService {
  private readonly STORAGE_KEY = 'translation_backups';
  private readonly METADATA_KEY = 'translation_backup_metadata';
  private readonly MAX_SNAPSHOTS = 10; // Reduced from 50 to prevent quota issues
  private readonly MAX_STORAGE_SIZE = 4 * 1024 * 1024; // 4MB limit (safe buffer under 5MB quota)
  private readonly AUTO_BACKUP_INTERVAL = 3600000; // 1 hour
  private autoBackupTimer: NodeJS.Timeout | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      this.initializeAutoBackup();
    }
  }

  /**
   * Generate checksum for translations to detect changes
   */
  private generateChecksum(data: Record<Language, Translations>): string {
    const str = JSON.stringify(data);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Get all snapshots from storage
   */
  private getSnapshots(): TranslationSnapshot[] {
    if (typeof window === 'undefined') return [];
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to retrieve snapshots:', error);
      return [];
    }
  }

  /**
   * Check if data size is within limits
   */
  private isWithinSizeLimit(data: string): boolean {
    const sizeInBytes = new Blob([data]).size;
    return sizeInBytes < this.MAX_STORAGE_SIZE;
  }

  /**
   * Clear old snapshots to free up space
   */
  private clearOldSnapshots(keepCount: number = 5): void {
    if (typeof window === 'undefined') return;
    try {
      const snapshots = this.getSnapshots();
      const trimmed = snapshots.slice(-keepCount);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(trimmed));
      console.log(`[Translation Backup] Cleared old snapshots, kept ${keepCount} most recent`);
    } catch (error) {
      console.error('Failed to clear old snapshots:', error);
    }
  }

  /**
   * Save snapshots to storage
   */
  private saveSnapshots(snapshots: TranslationSnapshot[]): void {
    if (typeof window === 'undefined') return;
    try {
      // Keep only the most recent snapshots
      const trimmed = snapshots.slice(-this.MAX_SNAPSHOTS);
      const dataToSave = JSON.stringify(trimmed);
      
      // Check size before saving
      if (!this.isWithinSizeLimit(dataToSave)) {
        console.warn('[Translation Backup] Data exceeds size limit, reducing snapshots');
        // Try with fewer snapshots
        const reducedSnapshots = snapshots.slice(-5);
        const reducedData = JSON.stringify(reducedSnapshots);
        
        if (!this.isWithinSizeLimit(reducedData)) {
          console.error('[Translation Backup] Even reduced data exceeds limit, keeping only 2 snapshots');
          const minimalSnapshots = snapshots.slice(-2);
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(minimalSnapshots));
          this.updateMetadata({ totalSnapshots: minimalSnapshots.length, lastBackup: Date.now() });
          return;
        }
        
        localStorage.setItem(this.STORAGE_KEY, reducedData);
        this.updateMetadata({ totalSnapshots: reducedSnapshots.length, lastBackup: Date.now() });
        return;
      }
      
      localStorage.setItem(this.STORAGE_KEY, dataToSave);
      this.updateMetadata({ totalSnapshots: trimmed.length, lastBackup: Date.now() });
    } catch (error) {
      if (error instanceof DOMException && (error as DOMException).name === 'QuotaExceededError') {
        console.error('[Translation Backup] Storage quota exceeded, clearing old snapshots');
        // Emergency cleanup: keep only 2 most recent snapshots
        this.clearOldSnapshots(2);
        
        // Try saving again with minimal snapshots
        try {
          const minimal = snapshots.slice(-2);
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(minimal));
          this.updateMetadata({ totalSnapshots: minimal.length, lastBackup: Date.now() });
        } catch (retryError) {
          console.error('[Translation Backup] Failed to save even after cleanup:', retryError);
          // Last resort: clear all backups
          localStorage.removeItem(this.STORAGE_KEY);
        }
      } else {
        console.error('Failed to save snapshots:', error);
      }
    }
  }

  /**
   * Get backup metadata
   */
  private getMetadata(): BackupMetadata {
    if (typeof window === 'undefined') {
      return {
        totalSnapshots: 0,
        lastBackup: 0,
        lastRestoration: null,
        autoBackupEnabled: true,
      };
    }
    try {
      const stored = localStorage.getItem(this.METADATA_KEY);
      return stored
        ? JSON.parse(stored)
        : {
            totalSnapshots: 0,
            lastBackup: 0,
            lastRestoration: null,
            autoBackupEnabled: true,
          };
    } catch (error) {
      console.error('Failed to retrieve metadata:', error);
      return {
        totalSnapshots: 0,
        lastBackup: 0,
        lastRestoration: null,
        autoBackupEnabled: true,
      };
    }
  }

  /**
   * Update backup metadata
   */
  private updateMetadata(updates: Partial<BackupMetadata>): void {
    if (typeof window === 'undefined') return;
    try {
      const current = this.getMetadata();
      const updated = { ...current, ...updates };
      localStorage.setItem(this.METADATA_KEY, JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to update metadata:', error);
    }
  }

  /**
   * Create a new snapshot of current translations
   */
  createSnapshot(source: 'auto' | 'manual' | 'pre-update' = 'manual'): TranslationSnapshot {
    const snapshot: TranslationSnapshot = {
      id: `snapshot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      translations: JSON.parse(JSON.stringify(translations)),
      checksum: this.generateChecksum(translations),
      source,
    };

    const snapshots = this.getSnapshots();
    snapshots.push(snapshot);
    this.saveSnapshots(snapshots);

    console.log(`[Translation Backup] Snapshot created: ${snapshot.id} (${source})`);
    return snapshot;
  }

  /**
   * Get the most recent snapshot
   */
  getLatestSnapshot(): TranslationSnapshot | null {
    const snapshots = this.getSnapshots();
    return snapshots.length > 0 ? snapshots[snapshots.length - 1] : null;
  }

  /**
   * Get snapshot by ID
   */
  getSnapshotById(id: string): TranslationSnapshot | null {
    const snapshots = this.getSnapshots();
    return snapshots.find((s) => s.id === id) || null;
  }

  /**
   * Get all snapshots (for history view)
   */
  getAllSnapshots(): TranslationSnapshot[] {
    return this.getSnapshots();
  }

  /**
   * Restore translations from a snapshot
   */
  restoreFromSnapshot(snapshotId: string): boolean {
    const snapshot = this.getSnapshotById(snapshotId);
    if (!snapshot) {
      console.error(`[Translation Backup] Snapshot not found: ${snapshotId}`);
      return false;
    }

    try {
      // Update the translations object
      Object.keys(snapshot.translations).forEach((lang) => {
        const language = lang as Language;
        Object.keys(snapshot.translations[language]).forEach((key) => {
          (translations[language] as any)[key] = snapshot.translations[language][key];
        });
      });

      this.updateMetadata({ lastRestoration: Date.now() });
      console.log(`[Translation Backup] Restored from snapshot: ${snapshotId}`);
      return true;
    } catch (error) {
      console.error('[Translation Backup] Restoration failed:', error);
      return false;
    }
  }

  /**
   * Detect missing or reverted translations
   */
  detectTranslationIssues(): {
    missingKeys: { language: Language; keys: string[] }[];
    revertedKeys: { language: Language; key: string; current: string; expected: string }[];
    totalIssues: number;
  } {
    const latestSnapshot = this.getLatestSnapshot();
    if (!latestSnapshot) {
      return { missingKeys: [], revertedKeys: [], totalIssues: 0 };
    }

    const missingKeys: { language: Language; keys: string[] }[] = [];
    const revertedKeys: { language: Language; key: string; current: string; expected: string }[] = [];

    const languages: Language[] = ['en', 'ar', 'fa', 'ru'];

    languages.forEach((lang) => {
      const missing: string[] = [];
      const snapshotKeys = Object.keys(latestSnapshot.translations[lang]);
      const currentKeys = Object.keys(translations[lang]);

      // Check for missing keys
      snapshotKeys.forEach((key) => {
        if (!currentKeys.includes(key) || !translations[lang][key]) {
          missing.push(key);
        }
      });

      if (missing.length > 0) {
        missingKeys.push({ language: lang, keys: missing });
      }

      // Check for reverted translations (comparing with English as baseline)
      if (lang !== 'en') {
        snapshotKeys.forEach((key) => {
          const currentValue = translations[lang][key];
          const snapshotValue = latestSnapshot.translations[lang][key];
          const englishValue = translations['en'][key];

          // If current value matches English but snapshot had a different translation
          if (
            currentValue === englishValue &&
            snapshotValue !== englishValue &&
            snapshotValue !== currentValue
          ) {
            revertedKeys.push({
              language: lang,
              key,
              current: currentValue,
              expected: snapshotValue,
            });
          }
        });
      }
    });

    const totalIssues = missingKeys.reduce((sum, item) => sum + item.keys.length, 0) + revertedKeys.length;

    return { missingKeys, revertedKeys, totalIssues };
  }

  /**
   * Auto-restore missing or reverted translations
   */
  autoRestore(): {
    restored: boolean;
    restoredCount: number;
    issues: ReturnType<typeof this.detectTranslationIssues>;
  } {
    const issues = this.detectTranslationIssues();

    if (issues.totalIssues === 0) {
      return { restored: false, restoredCount: 0, issues };
    }

    const latestSnapshot = this.getLatestSnapshot();
    if (!latestSnapshot) {
      return { restored: false, restoredCount: 0, issues };
    }

    let restoredCount = 0;

    // Restore missing keys
    issues.missingKeys.forEach(({ language, keys }) => {
      keys.forEach((key) => {
        const restoredValue = latestSnapshot.translations[language][key];
        if (restoredValue) {
          (translations[language] as any)[key] = restoredValue;
          restoredCount++;
        }
      });
    });

    // Restore reverted keys
    issues.revertedKeys.forEach(({ language, key, expected }) => {
      (translations[language] as any)[key] = expected;
      restoredCount++;
    });

    if (restoredCount > 0) {
      console.log(`[Translation Backup] Auto-restored ${restoredCount} translations`);
      this.updateMetadata({ lastRestoration: Date.now() });
    }

    return { restored: true, restoredCount, issues };
  }

  /**
   * Initialize automatic backup system
   */
  private initializeAutoBackup(): void {
    const metadata = this.getMetadata();
    if (!metadata.autoBackupEnabled) return;

    // Create initial snapshot if none exists
    const snapshots = this.getSnapshots();
    if (snapshots.length === 0) {
      this.createSnapshot('auto');
    }

    // Set up periodic backups
    this.autoBackupTimer = setInterval(() => {
      const currentChecksum = this.generateChecksum(translations);
      const latestSnapshot = this.getLatestSnapshot();

      // Only create backup if translations have changed
      if (!latestSnapshot || latestSnapshot.checksum !== currentChecksum) {
        this.createSnapshot('auto');
      }
    }, this.AUTO_BACKUP_INTERVAL);
  }

  /**
   * Enable/disable automatic backups
   */
  setAutoBackup(enabled: boolean): void {
    this.updateMetadata({ autoBackupEnabled: enabled });

    if (enabled && !this.autoBackupTimer) {
      this.initializeAutoBackup();
    } else if (!enabled && this.autoBackupTimer) {
      clearInterval(this.autoBackupTimer);
      this.autoBackupTimer = null;
    }
  }

  /**
   * Get backup statistics
   */
  getStats(): BackupMetadata & { oldestSnapshot: number | null } {
    const metadata = this.getMetadata();
    const snapshots = this.getSnapshots();
    const oldestSnapshot = snapshots.length > 0 ? snapshots[0].timestamp : null;

    return { ...metadata, oldestSnapshot };
  }

  /**
   * Clear all backups
   */
  clearAllBackups(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.METADATA_KEY);
    console.log('[Translation Backup] All backups cleared');
  }

  /**
   * Export backups as JSON
   */
  exportBackups(): string {
    const snapshots = this.getSnapshots();
    const metadata = this.getMetadata();
    return JSON.stringify({ snapshots, metadata }, null, 2);
  }

  /**
   * Import backups from JSON
   */
  importBackups(jsonData: string): boolean {
    try {
      const data = JSON.parse(jsonData);
      if (data.snapshots && Array.isArray(data.snapshots)) {
        this.saveSnapshots(data.snapshots);
        if (data.metadata) {
          this.updateMetadata(data.metadata);
        }
        console.log('[Translation Backup] Backups imported successfully');
        return true;
      }
      return false;
    } catch (error) {
      console.error('[Translation Backup] Import failed:', error);
      return false;
    }
  }
}

// Singleton instance
export const translationBackupService = new TranslationBackupService();

// Auto-detect and restore on initialization
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    const result = translationBackupService.autoRestore();
    if (result.restored && result.restoredCount > 0) {
      console.log(
        `[Translation Backup] Auto-restored ${result.restoredCount} translations on page load`
      );
    }
  });
}
